import { FunctionComponent } from 'react';

import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingContainer = styled.div<{ $inline?: boolean; $size?: 'small' | 'medium' | 'large' }>`
  display: ${({ $inline }) => ($inline ? 'inline-flex' : 'flex')};
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: ${({ $inline }) => ($inline ? '0' : '16px')};
`;

const Spinner = styled.div<{ $size?: 'small' | 'medium' | 'large' }>`
  width: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '12px';
      case 'large':
        return '24px';
      default:
        return '16px';
    }
  }};
  height: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '12px';
      case 'large':
        return '24px';
      default:
        return '16px';
    }
  }};
  border: 2px solid #dee2e6;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.span<{ $size?: 'small' | 'medium' | 'large' }>`
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '12px';
      case 'large':
        return '16px';
      default:
        return '14px';
    }
  }};
  color: #6c757d;
`;

export interface LoadingIndicatorProps {
  /** Loading message to display */
  message?: string;
  /** Size of the loading indicator */
  size?: 'small' | 'medium' | 'large';
  /** Whether to display inline (for use within text/buttons) */
  inline?: boolean;
  /** Custom className for styling */
  className?: string;
  /** Whether to show only the spinner without text */
  spinnerOnly?: boolean;
}

/**
 * Reusable loading indicator component
 *
 * Can be used for buttons, form fields, or standalone loading states.
 * Supports different sizes and inline/block display modes.
 *
 * @example
 * // Button loading state
 * <Button disabled={loading}>
 *   {loading ? <LoadingIndicator message="Saving..." inline spinnerOnly /> : "Save"}
 * </Button>
 *
 * @example
 * // Form field loading
 * <LoadingIndicator message="Loading users..." size="small" />
 *
 * @example
 * // Page loading
 * <LoadingIndicator message="Loading page..." size="large" />
 */
export const LoadingIndicator: FunctionComponent<LoadingIndicatorProps> = ({
  message = 'Loading...',
  size = 'medium',
  inline = false,
  className,
  spinnerOnly = false,
}) => {
  return (
    <LoadingContainer $inline={inline} $size={size} className={className}>
      <Spinner $size={size} />
      {!spinnerOnly && <LoadingText $size={size}>{message}</LoadingText>}
    </LoadingContainer>
  );
};
